import type {
	ExteriorApplicationDetail,
	ExteriorImprovementsReport,
	ExteriorImprovementsReportParams,
} from '@/services/data-service';
import type { ExportColumn } from '@/utils/accounting-export';
import { etDayMs, formatBusinessDateIso } from '@/utils/date-utils';
import { previousMonthRange, previousQuarterRange, previousYearRange, yearToDate } from '@/utils/report-dates';

export type ReportPreset = 'last-month' | 'last-quarter' | 'ytd' | 'last-year' | 'custom';

export const REPORT_PRESETS: { key: ReportPreset; label: string }[] = [
	{ key: 'last-month', label: 'Last month' },
	{ key: 'last-quarter', label: 'Last quarter' },
	{ key: 'ytd', label: 'Year to date' },
	{ key: 'last-year', label: 'Last year' },
	{ key: 'custom', label: 'Custom' },
];

const toIso = (d: Date): string => formatBusinessDateIso(d.getTime());

/** ET-anchored `{from, to}` for a preset; `custom` has none. */
export const presetRange = (preset: ReportPreset, now: Date = new Date()): { from: string; to: string } | null => {
	const ranges: Record<Exclude<ReportPreset, 'custom'>, () => { start: Date; end: Date }> = {
		'last-month': () => previousMonthRange(now),
		'last-quarter': () => previousQuarterRange(now),
		ytd: () => yearToDate(now),
		'last-year': () => previousYearRange(now),
	};
	if (preset === 'custom') return null;
	const { start, end } = ranges[preset]();
	return { from: toIso(start), to: toIso(end) };
};

/**
 * Sorted, deduped integer status codes. The list lands in a TanStack key, so `[5, 4]` and
 * `[4, 5]` must be the same value or they become two cache entries and a refetch.
 */
export const normalizeStatuses = (statuses: readonly number[]): number[] =>
	[...new Set(statuses.filter((s) => Number.isInteger(s)))].sort((a, b) => a - b);

/** A `status` URL param (`"5,4"`) → canonical status list. */
export const statusesFromParam = (param: string | undefined): number[] =>
	normalizeStatuses((param ?? '').split(',').filter(Boolean).map(Number));

export const exteriorImprovementsReportQuery =(p: ExteriorImprovementsReportParams): string => {
	const q = new URLSearchParams({ from: p.from, to: p.to, dateField: p.dateField });
	if (p.associationId) q.set('associationId', p.associationId);
	if (p.statuses?.length) q.set('status', p.statuses.join(','));
	if (p.detail) q.set('detail', '1');
	return q.toString();
};

export type ExteriorReportRow = ExteriorApplicationDetail & { associationName: string };

export const flattenReportRows = (report: ExteriorImprovementsReport): ExteriorReportRow[] =>
	report.associations.flatMap((a) => a.items.map((item) => ({ ...item, associationName: a.name })));

/** A real moment (ISO string or ms) → its ET business day, for date cells. */
export const momentToBusinessMs = (v: string | number | null | undefined): number | null => {
	if (v == null) return null;
	const ms = typeof v === 'number' ? v : Date.parse(v);
	return Number.isFinite(ms) ? etDayMs(ms) : null;
};

export const EXTERIOR_REPORT_COLUMNS: ExportColumn<ExteriorReportRow>[] = [
	{ header: 'App #', value: (r) => r.applicationNumber ?? '', format: 'integer', width: 60 },
	{ header: 'Association', value: (r) => r.associationName, width: 220 },
	{ header: 'Lot / Account', value: (r) => r.accountNumber ?? '', width: 90 },
	{ header: 'Property address', value: (r) => r.propertyAddress ?? r.accountAddress ?? '', width: 200 },
	{ header: 'Owner', value: (r) => r.accountOwnerName ?? r.submitterName ?? '', width: 160 },
	{ header: 'Request type', value: (r) => r.requestTypeName ?? '', width: 140 },
	{ header: 'Status', value: (r) => r.statusLabel ?? '', width: 140 },
	{ header: 'Submitted', value: (r) => momentToBusinessMs(r.createdAt), format: 'date', width: 90 },
	{ header: 'Decided', value: (r) => momentToBusinessMs(r.decidedAt), format: 'date', width: 90 },
	{ header: 'Decided by', value: (r) => r.decidedByName ?? '', width: 130 },
	{ header: 'Board meeting', value: (r) => r.boardMeetingDate ?? null, format: 'date', width: 100 },
	{ header: 'Description', value: (r) => r.description ?? '', width: 320 },
];
